import React, { useState, useEffect } from 'react';
import { CalendarIcon, ClockIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { analyticsAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import toast from 'react-hot-toast';

interface SimpleGanttProps {
  className?: string;
}

const SimpleGantt: React.FC<SimpleGanttProps> = ({ className = '' }) => {
  const [ganttData, setGanttData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  useEffect(() => {
    loadGanttData();
  }, []);

  const loadGanttData = async () => {
    try {
      setIsLoading(true);
      const data = await analyticsAPI.getGanttData();
      setGanttData(data);
    } catch (error) {
      console.error('Error loading Gantt data:', error);
      toast.error('Failed to load timeline data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: string | Date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PLANNING': return 'bg-purple-100 text-purple-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'ON_HOLD': return 'bg-orange-100 text-orange-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'bg-red-500';
      case 'NORMAL': return 'bg-blue-500';
      case 'LOW': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const calculateDuration = (start: string | Date, end: string | Date) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getProgressWidth = (progress: number) => {
    return Math.min(Math.max(progress, 0), 100);
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (ganttData.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="text-center py-12">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Timeline Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            No projects with timeline data found. Create projects with start and end dates to see the timeline.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Project Timeline</h3>
            <p className="text-sm text-gray-500">
              Visual timeline showing project schedules and progress
            </p>
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="p-6">
        <div className="space-y-6">
          {ganttData.map((project: any) => (
            <div key={project.id} className="border border-gray-200 rounded-lg p-4">
              {/* Project Header */}
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setSelectedProject(selectedProject?.id === project.id ? null : project)}
              >
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: project.color || '#3B82F6' }}
                  />
                  <div>
                    <h4 className="font-medium text-gray-900">{project.name}</h4>
                    <p className="text-sm text-gray-500">
                      {formatDate(project.start)} - {formatDate(project.end)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{project.progress || 0}% Complete</p>
                    <p className="text-xs text-gray-500">{project.tasks?.length || 0} tasks</p>
                  </div>
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${getProgressWidth(project.progress || 0)}%`,
                        backgroundColor: project.color || '#3B82F6'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Project Details */}
              {selectedProject?.id === project.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500">Duration</label>
                      <p className="text-sm text-gray-900">
                        {calculateDuration(project.start, project.end)} days
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Teams</label>
                      <p className="text-sm text-gray-900">
                        {project.teams?.join(', ') || 'No teams assigned'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Progress</label>
                      <p className="text-sm text-gray-900">{project.progress || 0}%</p>
                    </div>
                  </div>

                  {/* Tasks */}
                  {project.tasks && project.tasks.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Tasks</h5>
                      <div className="space-y-2">
                        {project.tasks.map((task: any) => (
                          <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div 
                                className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`}
                                title={`${task.priority} Priority`}
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{task.name}</p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(task.start)} - {formatDate(task.end)}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              {task.assignees && task.assignees.length > 0 && (
                                <div className="text-xs text-gray-500">
                                  {task.assignees.slice(0, 2).join(', ')}
                                  {task.assignees.length > 2 && ` +${task.assignees.length - 2}`}
                                </div>
                              )}
                              <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full ${getPriorityColor(task.priority)}`}
                                  style={{ width: `${getProgressWidth(task.progress || 0)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 w-8 text-right">
                                {task.progress || 0}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="px-6 pb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Timeline Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{ganttData.length}</div>
              <div className="text-gray-500">Total Projects</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">
                {ganttData.reduce((sum, project) => sum + (project.tasks?.length || 0), 0)}
              </div>
              <div className="text-gray-500">Total Tasks</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">
                {ganttData.filter(project => project.progress >= 100).length}
              </div>
              <div className="text-gray-500">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">
                {ganttData.length > 0 
                  ? Math.round(ganttData.reduce((sum, project) => sum + (project.progress || 0), 0) / ganttData.length)
                  : 0}%
              </div>
              <div className="text-gray-500">Avg Progress</div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Legend</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-gray-700">High Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-gray-700">Normal Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-gray-700">Low Priority</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleGantt;
